import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQueries, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api'
import { ApiResponse, ApiError } from '../types/api'
import { Project, loadingLayers } from '../types/project'
import {
    ProjectDefaultTreatmentCost,
    UpdateProjectDefaultTreatmentCostInput,
} from '../types/projectDefaultTreatmentCost'
import { bumpVegetationTileVersion } from './vegetation'
import { StrategyContext } from '../contexts/StrategyContext'

export const useProject = () => {
    const { projectId } = useParams();
    const { data: project, isLoading, error } = useQuery({
        queryKey: ['project', projectId],
        queryFn:  () => api.request<ApiResponse<Project>>(`/api/projects/${projectId}/`),
        enabled: !!projectId,
        refetchOnWindowFocus: false,
        refetchInterval: (query) => {
            const data = query.state.data?.data;
            const status = data?.status;
            if (status === 'processing' || status === 'expanding' || status === 'refreshing') return 3000;
            // Open, but still loading layers behind the map (LayerStatusBar).
            return loadingLayers(data?.layer_status).length > 0 ? 4000 : false;
        },
    }) as { data: ApiResponse<Project>, isLoading: boolean, error: ApiError };

    return { project, isLoading, error };
};

/**
 * One project by id, for pages that are not under a /projects/:projectId
 * route (story maps that belong to a folder or to the whole organisation).
 * Same cache entry as useProject(), without its polling.
 */
export const useProjectById = (projectId: number | string | null | undefined) => {
    const idKey = projectId !== null && projectId !== undefined ? String(projectId) : undefined;
    return useQuery<ApiResponse<Project>, ApiError>({
        queryKey: ['project', idKey],
        queryFn: () => api.request<ApiResponse<Project>>(`/api/projects/${idKey}/`),
        enabled: !!idKey,
        refetchOnWindowFocus: false,
    });
};

/** Several projects by id (same cache entries as useProject()), keyed by id. */
export const useProjectsByIds = (
    projectIds: (number | string)[]
): { byId: Record<string, Project>; isPending: boolean } => {
    const keys = useMemo(() => projectIds.map((id) => String(id)), [projectIds]);
    return useQueries({
        queries: keys.map((idKey) => ({
            queryKey: ['project', idKey],
            queryFn: () => api.request<ApiResponse<Project>>(`/api/projects/${idKey}/`),
            refetchOnWindowFocus: false as const,
        })),
        combine: (results) => {
            const byId: Record<string, Project> = {};
            results.forEach((q, i) => {
                const project = q.data?.data;
                if (project) byId[keys[i]] = project;
            });
            return { byId, isPending: results.some((q) => q.isPending) };
        },
    });
};

export const useProjectDefaultTreatmentCosts = (projectId: string | undefined) => {
    return useQuery({
        queryKey: ['project', projectId, 'default-treatment-costs'],
        queryFn: async () =>
            api.request<ApiResponse<ProjectDefaultTreatmentCost[]>>(
                `/api/projects/${projectId}/default-treatment-costs`
            ),
        enabled: !!projectId,
        refetchOnWindowFocus: false,
    });
};

export const useUpdateProjectDefaultTreatmentCosts = (projectId: string) => {
    const queryClient = useQueryClient();

    return useMutation<
        ApiResponse<ProjectDefaultTreatmentCost[]>,
        ApiError,
        UpdateProjectDefaultTreatmentCostInput[]
    >({
        mutationFn: async (defaults) =>
            api.request<ApiResponse<ProjectDefaultTreatmentCost[]>>(
                `/api/projects/${projectId}/default-treatment-costs`,
                {
                    method: 'PUT',
                    body: { defaults },
                }
            ),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['project', projectId, 'default-treatment-costs'],
            });
        },
    });
};

export type ExpandDirection = "north" | "south" | "east" | "west";
export interface ExpandPayload { direction: ExpandDirection; tile_col: number; tile_row: number; }

export const useExpandProject = (projectId: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation<ApiResponse<Project>, ApiError, ExpandPayload>({
        mutationFn: (payload) =>
            api.request<ApiResponse<Project>>(`/api/projects/${projectId}/expand`, {
                method: "POST",
                body: payload,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["project", projectId] });
            queryClient.invalidateQueries({ queryKey: ["projects"] });
        },
    });
};

export const useUpdateProjectAutoAgent = (
    projectId: string | number | undefined
) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;
    const mutationKey = ['project-auto-agent', idKey];

    return useMutation<
        ApiResponse<Project>,
        ApiError,
        boolean,
        { previous?: ApiResponse<Project> }
    >({
        mutationKey,
        mutationFn: (auto_agent_incidents) =>
            api.patch<ApiResponse<Project>>(`/api/projects/${idKey}/`, {
                auto_agent_incidents,
            }),
        onMutate: async (auto_agent_incidents) => {
            if (!idKey) return {};
            await queryClient.cancelQueries({ queryKey: ['project', idKey] });
            const previous = queryClient.getQueryData<ApiResponse<Project>>([
                'project',
                idKey,
            ]);
            if (previous?.data) {
                queryClient.setQueryData<ApiResponse<Project>>(['project', idKey], {
                    ...previous,
                    data: { ...previous.data, auto_agent_incidents },
                });
            }
            return { previous };
        },
        onError: (_error, _variables, context) => {
            if (idKey && context?.previous) {
                queryClient.setQueryData(['project', idKey], context.previous);
            }
        },
        onSettled: () => {
            if (queryClient.isMutating({ mutationKey }) > 1) return;
            queryClient.invalidateQueries({ queryKey: ['project', idKey] });
        },
    });
};

/**
 * Project-specific standing orders for the AI incident controller (#312),
 * added to the organisation-wide ones (Admin → Incident Agent). Saved
 * explicitly rather than optimistically: this is free text a person is still
 * typing, and a failed save must leave their words on screen.
 */
export const useUpdateProjectAgentInstructions = (
    projectId: string | number | undefined
) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;

    return useMutation<ApiResponse<Project>, ApiError, string>({
        mutationFn: (agent_instructions) =>
            api.patch<ApiResponse<Project>>(`/api/projects/${idKey}/`, {
                agent_instructions,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', idKey] });
        },
    });
};

/**
 * The strategy that describes the estate as it is. The incident agent's
 * simulations run on it and "what does this fire touch" reads its
 * treatments (revegetation compartments = plantations); null means the most
 * recently updated strategy. Saved immediately, like the auto-monitor switch.
 */
export const useUpdateProjectOperationalStrategy = (
    projectId: string | number | undefined
) => {
    const queryClient = useQueryClient();
    const idKey = projectId !== undefined ? String(projectId) : undefined;

    return useMutation<ApiResponse<Project>, ApiError, number | null>({
        mutationFn: (operational_strategy_id) =>
            api.patch<ApiResponse<Project>>(`/api/projects/${idKey}/`, {
                operational_strategy_id,
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['project', idKey] });
        },
    });
};

interface UpdateProjectData {
    name?: string;
    description?: string;
    resolution?: number;
    grid_size?: number;
    /** FK to ConstructionType (or null to clear). camelCase to match backend. */
    defaultConstructionTypeId?: number | null;
    /** Crown packing overrides, metres; the whole object replaces the stored one ({} = defaults). */
    crown_settings?: Record<string, number>;
}

export const useUpdateProject = (projectId: string) => {
    const queryClient = useQueryClient();
    const { refreshStrategy } = useContext(StrategyContext);

    return useMutation<ApiResponse<Project>, ApiError, UpdateProjectData>({
        mutationFn: async (data: UpdateProjectData) => {
            let response;

            // First update project details. Only include keys the caller
            // actually supplied so we don't accidentally clobber others.
            const projectData: Record<string, any> = {};
            if (data.name !== undefined) projectData.name = data.name;
            if (data.description !== undefined) projectData.description = data.description;
            if (data.resolution !== undefined) projectData.resolution = data.resolution;
            if (data.grid_size !== undefined) projectData.grid_size = data.grid_size;
            if (data.defaultConstructionTypeId !== undefined) {
                projectData.defaultConstructionTypeId = data.defaultConstructionTypeId;
            }
            if (data.crown_settings !== undefined) projectData.crown_settings = data.crown_settings;

            response = await api.request<ApiResponse<Project>>(
                `/api/projects/${projectId}/`,
                {
                    method: 'PATCH',
                    body: projectData,
                }
            );

            // No fuel disruptions file handling here - it's moved to a separate hook

            return response;
        },
        onSuccess: () => {
            // Invalidate both single project and projects list queries
            queryClient.invalidateQueries({ queryKey: ['project', projectId] });
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
            queryClient.invalidateQueries({ queryKey: ['strategy'] });
            bumpVegetationTileVersion(queryClient, projectId);
            refreshStrategy();

            // Force refetch both queries
            queryClient.refetchQueries({ queryKey: ['project', projectId], exact: true, type: 'active' });
            queryClient.refetchQueries({ queryKey: ['projects'], exact: true, type: 'active' });
        },
    });
};
